import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect } from 'react';

import type {
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetOfferRequestConditionsLatest,
} from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { BankOfferResults } from './RequestConfiguration/BankOfferResults';
import type { getRuntimeOptions } from './RequestConfiguration/util/getRuntimeOptions';
import Spinner from '~/components/icons/Spinner';
import { RequestForm } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RequestForm.tsx';

const OFFER_CONDITION_ERROR_TOAST_ID = 'offer-request-conditions-latest-error';

type Props = {
    isLoading: boolean;
    isError: boolean;
    currentRequestId?: string;
    setCurrentRequestId: Dispatch<SetStateAction<string | undefined>>;
    lead: SchemaLoanRequestResolver_Get;
    requestConditions: SchemaLoanRequestResolver_GetOfferRequestConditionsLatest | undefined;
    runtimeOptions: ReturnType<typeof getRuntimeOptions>;
};

export const NewCreditRequest: React.FC<Props> = ({
    currentRequestId,
    isError,
    isLoading,
    lead,
    requestConditions,
    runtimeOptions,
    setCurrentRequestId,
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        if (isError) {
            toast.error(t('BANK_CREDIT_REQUEST.OFFER_CONDITION_LOADING_FAILED'), {
                toastId: OFFER_CONDITION_ERROR_TOAST_ID,
            });
        }
    }, [isError, t]);

    if (isLoading)
        return (
            <div className="w-full inset-0 flex items-center justify-center z-50">
                <Spinner />
            </div>
        );
    if (isError) return null;
    return (
        <>
            <RequestForm
                requestConditions={requestConditions}
                currentRequestId={currentRequestId}
                lead={lead}
                setCurrentRequestId={setCurrentRequestId}
                runtimeOptions={runtimeOptions}
            />

            <BankOfferResults currentRequestId={currentRequestId} lead={lead} />
        </>
    );
};
